/**
 * Pure, dependency-free constants shared by the schema, the Worker and the UI.
 * Kept out of src/db/schema.ts so the browser bundle never has to pull
 * drizzle-orm in just to render a list of status names.
 */

export const ARTICLE_STATUSES = [
  'PENDING',
  'PUBLISHED',
  'REJECTED_PLAGIARISM',
  'REJECTED_FAKE',
  'REJECTED_POLICY',
] as const;

export type ArticleStatus = (typeof ARTICLE_STATUSES)[number];

/**
 * Region colours drive the article card border, the globe point colour and the
 * legend. Free-form text in the DB per spec, recommended set listed here.
 */
export const REGION_COLORS = [
  'red',
  'blue',
  'yellow',
  'green',
  'purple',
  'orange',
  'cyan',
] as const;

export type RegionColor = (typeof REGION_COLORS)[number];

/**
 * The five pillars, each with the colour the Guardian uses for that desk.
 * Sections live underneath: the header shows the active pillar's subsections.
 */
export const PILLARS = [
  { id: 'news', label: 'News', color: '#C70000' },
  { id: 'opinion', label: 'Opinion', color: '#E05E00' },
  { id: 'sport', label: 'Sport', color: '#0077B6' },
  { id: 'culture', label: 'Culture', color: '#A1845C' },
  { id: 'lifestyle', label: 'Lifestyle', color: '#BB3B80' },
] as const;

export type PillarId = (typeof PILLARS)[number]['id'];

/** Legacy flat desk retained for pre-pillar rows and the DB check constraint. */
export const ARTICLE_SECTIONS = ['politics', 'economy', 'technology', 'general'] as const;
export type ArticleSection = (typeof ARTICLE_SECTIONS)[number];

export const ARTICLE_PILLARS = PILLARS.map((pillar) => pillar.id);

export const SUBSECTIONS: Record<PillarId, readonly string[]> = {
  news: ['world', 'us-politics', 'uk', 'climate-crisis'],
  opinion: ['columnists', 'letters', 'editorial'],
  sport: ['football', 'cricket', 'olympics'],
  culture: ['books', 'music', 'tv-radio'],
  lifestyle: ['food', 'travel', 'wellbeing'],
};

export function pillarColor(pillar: string): string {
  return PILLARS.find((item) => item.id === pillar)?.color ?? '#C70000';
}

/**
 * Display labels for the colour codes (spec §5: red = CIS, blue = USA,
 * yellow = Asia). Cosmetic only — the database stores the colour, not the label.
 */
export const REGION_LABELS: Record<string, string> = {
  red: 'CIS',
  blue: 'North America',
  yellow: 'Asia',
  green: 'Africa',
  purple: 'Europe',
  orange: 'Middle East',
  cyan: 'Oceania',
};

export function regionLabel(color: string): string {
  return REGION_LABELS[color] ?? color;
}

/** WebGL cannot read Tailwind classes, so the palette is duplicated as hex. */
export const REGION_HEX: Record<string, string> = {
  red: '#ef4444',
  blue: '#3b82f6',
  yellow: '#eab308',
  green: '#22c55e',
  purple: '#a855f7',
  orange: '#f97316',
  cyan: '#06b6d4',
};

export function regionHex(color: string): string {
  return REGION_HEX[color] ?? '#9ca3af';
}
